import Alpine from 'alpinejs';
import { normalizeSearchBedrooms } from '../utils/searchBedrooms.js';

const SCROLL_FADE_SELECTOR = '.search-composer__scroll-fades';
const DESKTOP_PANEL_HEIGHT_PROPERTY = '--search-composer-viewport-max-height';
const DESKTOP_PANEL_BOTTOM_GAP_REM = 1.5;
const DESKTOP_PANEL_MIN_HEIGHT_REM = 12.5;

Alpine.data('searchComposer', (config = {}) => ({
    activePanel: null,
    mobileOpen: false,
    mobileStep: 'where',
    returnFocusTo: null,
    scrollFadeFrame: null,
    scrollFadeHandler: null,
    scrollFadeMutationObserver: null,
    scrollFadeResizeObserver: null,
    viewportChangeHandler: null,

    init() {
        if (config.visualWhere) {
            this.$store.search.mapAreaLabel = config.visualWhere;
            this.$store.search._visualWhereMatch =
                config.visualWhereMatch || null;
        }

        this.$watch('$store.search.ui.isSubmittingSearch', (isSubmitting) => {
            if (!isSubmitting) return;

            this.closeTabs();
        });

        this.$watch('activePanel', () => {
            this.$nextTick(() => this.scheduleScrollFadeRefresh());
        });

        this.$nextTick(() => this.initScrollFades());
    },

    destroy() {
        if (this.mobileOpen) this.$store.body.scrollLocked = false;

        if (this.scrollFadeHandler) {
            this.$root.removeEventListener(
                'scroll',
                this.scrollFadeHandler,
                true
            );
        }

        this.scrollFadeMutationObserver?.disconnect();
        this.scrollFadeResizeObserver?.disconnect();

        if (this.viewportChangeHandler) {
            window.removeEventListener(
                'scroll',
                this.viewportChangeHandler,
                true
            );
            window.removeEventListener('resize', this.viewportChangeHandler);
            window.visualViewport?.removeEventListener(
                'scroll',
                this.viewportChangeHandler
            );
            window.visualViewport?.removeEventListener(
                'resize',
                this.viewportChangeHandler
            );
        }

        if (this.scrollFadeFrame) {
            window.cancelAnimationFrame(this.scrollFadeFrame);
        }
    },

    initScrollFades() {
        this.viewportChangeHandler = (event) => {
            if (
                event.type === 'scroll' &&
                event.target instanceof Node &&
                this.$root.contains(event.target)
            )
                return;

            this.scheduleScrollFadeRefresh();
        };

        window.addEventListener('scroll', this.viewportChangeHandler, {
            capture: true,
            passive: true
        });
        window.addEventListener('resize', this.viewportChangeHandler);
        window.visualViewport?.addEventListener(
            'scroll',
            this.viewportChangeHandler
        );
        window.visualViewport?.addEventListener(
            'resize',
            this.viewportChangeHandler
        );

        this.scrollFadeHandler = (event) => {
            const scroller = event.target;

            if (
                scroller instanceof Element &&
                scroller.matches(SCROLL_FADE_SELECTOR)
            ) {
                this.updateScrollFades(scroller);
            }
        };

        this.$root.addEventListener('scroll', this.scrollFadeHandler, true);

        if ('ResizeObserver' in window) {
            this.scrollFadeResizeObserver = new ResizeObserver(() => {
                this.scheduleScrollFadeRefresh();
            });
            this.scrollFadeResizeObserver.observe(this.$root);
        }

        if ('MutationObserver' in window) {
            this.scrollFadeMutationObserver = new MutationObserver(() => {
                this.scheduleScrollFadeRefresh();
            });
            this.scrollFadeMutationObserver.observe(this.$root, {
                attributes: true,
                attributeFilter: ['class', 'style'],
                characterData: true,
                childList: true,
                subtree: true
            });
        }

        this.refreshScrollFades();
    },

    scheduleScrollFadeRefresh() {
        if (this.scrollFadeFrame) return;

        this.scrollFadeFrame = window.requestAnimationFrame(() => {
            this.scrollFadeFrame = null;
            this.refreshScrollFades();
        });
    },

    refreshScrollFades() {
        this.updateDesktopPanelHeight();

        this.$root
            .querySelectorAll(SCROLL_FADE_SELECTOR)
            .forEach((scroller) => {
                this.scrollFadeResizeObserver?.observe(scroller);
                this.updateScrollFades(scroller);
            });
    },

    updateDesktopPanelHeight() {
        const panel = this.activePanel
            ? this.$root.querySelector(
                  `[data-search-composer-panel="${this.activePanel}"]`
              )
            : null;

        if (
            !window.matchMedia('(min-width: 64rem)').matches ||
            !panel?.offsetWidth
        ) {
            this.$root.style.removeProperty(DESKTOP_PANEL_HEIGHT_PROPERTY);
            return;
        }

        const rootFontSize = parseFloat(
            getComputedStyle(document.documentElement).fontSize
        );
        const viewport = window.visualViewport;
        const viewportBottom = viewport
            ? viewport.offsetTop + viewport.height
            : window.innerHeight;
        const availableHeight =
            (viewportBottom - panel.getBoundingClientRect().top) /
                rootFontSize -
            DESKTOP_PANEL_BOTTOM_GAP_REM;

        if (availableHeight < DESKTOP_PANEL_MIN_HEIGHT_REM) {
            this.$root.style.removeProperty(DESKTOP_PANEL_HEIGHT_PROPERTY);
            return;
        }

        const height = `${Math.floor(availableHeight * 10000) / 10000}rem`;
        if (
            this.$root.style.getPropertyValue(DESKTOP_PANEL_HEIGHT_PROPERTY) !==
            height
        ) {
            this.$root.style.setProperty(DESKTOP_PANEL_HEIGHT_PROPERTY, height);
        }
    },

    updateScrollFades(scroller) {
        const maxScrollTop = Math.max(
            0,
            scroller.scrollHeight - scroller.clientHeight
        );
        const hasOverflow = maxScrollTop > 1;

        scroller.dataset.scrollBefore = String(
            hasOverflow && scroller.scrollTop > 1
        );
        scroller.dataset.scrollAfter = String(
            hasOverflow && scroller.scrollTop < maxScrollTop - 1
        );
    },

    openPanel(panel, trigger = null) {
        this.returnFocusTo = trigger;
        this.activePanel = this.activePanel === panel ? null : panel;
        this.$store.popovers.close();
    },

    closePanel({ restoreFocus = false } = {}) {
        this.activePanel = null;

        if (restoreFocus && typeof this.returnFocusTo?.focus === 'function') {
            this.$nextTick(() => this.returnFocusTo.focus());
        }
    },

    openMobile(step = 'where', trigger = null) {
        this.returnFocusTo = trigger;
        this.mobileStep = step;
        this.mobileOpen = true;
        this.$store.body.scrollLocked = true;
        this.$nextTick(() =>
            this.$root
                .querySelector('[data-search-composer-mobile-close]')
                ?.focus()
        );
    },

    closeMobile({ restoreFocus = true } = {}) {
        this.mobileOpen = false;
        this.$store.body.scrollLocked = false;
        this.$store.popovers.close();

        if (restoreFocus && typeof this.returnFocusTo?.focus === 'function') {
            this.$nextTick(() => this.returnFocusTo.focus());
        }
    },

    collapseMobileWho() {
        if (!this.mobileOpen || this.mobileStep !== 'who') return;

        this.mobileStep = 'review';
        this.$nextTick(() =>
            this.$root
                .querySelector('[data-search-composer-who-summary]')
                ?.focus({ preventScroll: true })
        );
    },

    closeTabs() {
        this.closePanel();
        this.closeMobile({ restoreFocus: false });
    },

    nextMobile() {
        if (this.mobileStep === 'where') {
            if (!this.$store.search.where) {
                this.$store.search.ui.validationMessage =
                    'Choose where you want to stay.';
                this.$nextTick(() =>
                    this.$root.querySelector('input[name="where"]')?.focus()
                );
                return;
            }

            this.$store.search.ui.validationMessage = null;
            this.mobileStep = 'when';
            return;
        }

        if (this.mobileStep === 'when') {
            const stayError =
                this.$store.search.collectionStayValidationMessage;
            if (stayError) {
                this.$store.search.ui.validationMessage = stayError;
                return;
            }
            this.mobileStep = 'who';
            return;
        }

        this.submit();
    },

    advanceAfterLocationSelection() {
        this.$store.search.ui.validationMessage = null;
        this.$store.popovers.close();

        if (this.mobileOpen) {
            this.mobileStep = 'when';
            return;
        }

        this.activePanel = 'when';
    },

    advanceAfterExactDates(selectedDates) {
        if (!Array.isArray(selectedDates) || selectedDates.length !== 2) return;

        const stayError = this.$store.search.collectionStayValidationMessage;
        if (stayError) {
            this.$store.search.ui.validationMessage = stayError;
            return;
        }

        this.$store.search.ui.validationMessage = null;
        this.$store.popovers.close();

        if (this.mobileOpen) {
            this.mobileStep = 'who';
            return;
        }

        this.activePanel = 'who';
    },

    clearWhere() {
        this.$store.search.where = '';
        this.$store.search.whereData = null;
        this.$store.search.composerDestinationSlug = null;
        this.$store.search.ui.validationMessage = null;
    },

    submit() {
        const stayError = this.$store.search.collectionStayValidationMessage;
        if (stayError) {
            this.$store.search.ui.validationMessage = stayError;
            this.openDates();
            return;
        }

        this.closeTabs();
        this.$store.search.submitSearch();
    },

    openDates() {
        if (window.matchMedia('(min-width: 64rem)').matches) {
            this.activePanel = 'when';
        } else {
            this.openMobile('when');
        }
    },

    get whenLabel() {
        return this.$store.search.dateMode === 'flexible'
            ? this.$store.search.flexibleDateLabel
            : this.$store.search.formattedDates || 'Add dates';
    },

    get whoLabel() {
        const guestLabel = this.$store.search.guestsLabel || 'Add guests';
        const bedrooms = normalizeSearchBedrooms(
            this.$store.search.filters.bedrooms
        );
        const bedroomLabel = `${bedrooms} ${bedrooms === 1 ? 'bedroom' : 'bedrooms'}`;

        return `${guestLabel}, ${bedroomLabel}`;
    },

    recentSearchSummary(search) {
        const details = [];
        if (search.dateMode === 'flexible' && search.flexibleMonth) {
            details.push(
                `${this.$store.search.formatFlexibleMonth(search.flexibleMonth)}, ${this.$store.search.formatFlexibleStayLength(search.flexibleNights, search.flexibleMaxNights)}`
            );
        } else if (search.dates) {
            details.push(this.$store.search.formatDateRange(search.dates));
        }

        const guests = Number(search.guests) || 1;
        const bedrooms = normalizeSearchBedrooms(search.bedrooms);
        details.push(`${guests} ${guests === 1 ? 'guest' : 'guests'}`);
        details.push(`${bedrooms} ${bedrooms === 1 ? 'bedroom' : 'bedrooms'}`);

        return details.join(' | ');
    }
}));
